import { Dispatch, SetStateAction, useState, useEffect, useContext } from "react"
import { useParams } from "react-router-dom"
import { TabPage } from "components/TabPage"
import { SessionContext } from "context/SessionProvider"
import { useContributionFeesQuery } from "hooks"
import { DigitalWalletType, Frequency, IUser, PaymentMethodType } from "modules/contribution/types"
import { digitalWalletAvailable, sendToMobileApp } from "utils"
import { amountValue, frequencyValue } from 'modules/contribution/LocalState'
import { GiftingAddStripeCard } from "./GiftingAddStripeCard"
import DigitalWalletView from "./GiftingDigitalWalletView"
import { GiftingLinkBankAccount } from "./GiftingLinkBankAccount"
import { GiftingNewPaymentMethod } from "./GiftingNewPaymentMethod"
import { GiftingSummaryHeader } from './GiftingSummaryHeader'
import { CurrentFlow } from 'modules/contribution/types'
import { isUpdateFlow } from "modules/contribution/helpers"
import { PaymentFailureContent } from 'modules/contribution/components/PaymentFailureModal'

const CURRENT_STEP = 'add-payment-method'

const GiftingAddPaymentMethod = ({
  fund,
  onSuccess,
  updateCurrentUser,
  user,
  eventName = "GiftingAddPaymentMethod",
  setShowModalNav
}: {
  fund?: {
    fundName: string,
    handle: string,
    giftingPage: {
      photoUrl: string
    }
    uuid: string
  }
  onSuccess: () => void
  updateCurrentUser: (attributes: object) => Promise<any>
  user: IUser,
  eventName?: string
  setShowModalNav?: Dispatch<SetStateAction<boolean>> | undefined
}) => {
  const { flow }: { flow: CurrentFlow } = useParams()
  const context = useContext(SessionContext)
  const [view, setView] = useState<PaymentMethodType>('bank-account')
  const [digitalWalletType, setDigitalWalletType] = useState<DigitalWalletType>('')
  const [linkError, setLinkError] = useState('')
 
  // 0 = 'bank-account'
  // 1 = 'credit-card'
  // 2 = 'digital-wallet'
  const [activeTab, setActiveTab] = useState<0 | 1 | 2>(0)

  const amount = amountValue()
  const frequency = frequencyValue()

  let input: {
    paymentMethodType: 'credit-card' | 'bank-account'
    frequency: Frequency
    amount: number,
    fundUuid?: string
  } = {
    paymentMethodType: "credit-card",
    frequency,
    amount, 
  }

  if (fund) {
    input.fundUuid = fund.uuid
  }
 
  const { subscriptionFee: feeForDigitalWallet, loading: feeForDigitalWalletLoading } = useContributionFeesQuery(input)

  useEffect(() => {
    if (isUpdateFlow(flow)) return

    if (context.mobileAppFromV(2)) {
      const root = window as any
      root.setDigitalWalletAvailable = (digitalWalletType: any) => {
        setDigitalWalletType(digitalWalletType)
      }
      sendToMobileApp(
        'digital_wallet_available',
        'window.setDigitalWalletAvailable',
      )
    } else {
      digitalWalletAvailable(setDigitalWalletType)
    }
  }, [flow])

  if (linkError) {
    setShowModalNav && setShowModalNav(false)

    return (
      <PaymentFailureContent
        errorMessage={linkError}
        close={() => {
          setShowModalNav && setShowModalNav(true)
          setLinkError('')
        }}
      />
    )
  }

  return (
    <>
      <GiftingSummaryHeader
        fund={fund}
      />
      <GiftingNewPaymentMethod
        view={view}
        setView={setView}
        setActiveTab={setActiveTab}
        digitalWalletType={digitalWalletType}
      >
        <TabPage
          activeTab={activeTab}
          index={0}
        >
          <GiftingLinkBankAccount
            currentStep={CURRENT_STEP}
            eventName={eventName}
            onSuccess={onSuccess}
            setLinkError={setLinkError}
          />
        </TabPage>
        <TabPage
          activeTab={activeTab}
          index={1}
        >
          <GiftingAddStripeCard
            updateCurrentUser={updateCurrentUser}
            user={user}
            onSuccess={onSuccess}
            eventName={eventName}
            currentStep={CURRENT_STEP}
          />

        </TabPage>
        <TabPage
          activeTab={activeTab}
          index={2}
        >
          <DigitalWalletView
            feeForDigitalWallet={feeForDigitalWallet}
            feeForDigitalWalletLoading={feeForDigitalWalletLoading}
            digitalWalletType={digitalWalletType}
            fund={fund}
            user={user}
          />

        </TabPage>
      </GiftingNewPaymentMethod>
    </>
  )
}

export default GiftingAddPaymentMethod

